import { useState, useEffect } from 'react';
import { OrderConsignments } from './Consignments.jsx';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiConfirm, uiPrompt, uiAlert } from '../utils/dialogs.js';
import PrintButton from '../components/shared/PrintButton.jsx';

// ── Primitives ────────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'opacity .15s', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1 };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 600, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}><label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>{children}</div>;
}
function Input(props) { return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>; }
function Textarea(props) { return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />; }
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>; }
function BtnRow({ children }) { return <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>{children}</div>; }

function ErrBanner({ msg }) { return msg ? <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p> : null; }

const STATUS_C = {
  DRAFT: { bg: '#f3f4f6', color: '#6b7280' }, CONFIRMED: { bg: '#dbeafe', color: '#1e40af' },
  PICKING: { bg: '#fef9c3', color: '#854d0e' }, DISPATCHED: { bg: '#dcfce7', color: '#166534' },
  DELIVERED: { bg: '#dcfce7', color: '#166534' }, CANCELLED: { bg: '#fee2e2', color: '#991b1b' },
  INVOICED: { bg: '#ede9fe', color: '#5b21b6' },
};

function StatusBadge({ status }) {
  const c = STATUS_C[status] || { bg: '#f3f4f6', color: '#6b7280' };
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.color }}>{status}</span>;
}

function fmt(d) { return d ? new Date(d).toLocaleDateString('en-GB') : '—'; }
function fmtCurrency(v) { return v != null ? `£${Number(v).toFixed(2)}` : '—'; }

// ── Outlet prices ─────────────────────────────────────────────────────────────
// The outlet's price-list price less its discount for that product. Tiered
// discounts go by the line's quantity, or the order's total of the product.
function discountOff(d, qty) {
  if (!d) return 0;
  let off = Number(d.flat) || 0;
  for (const t of [...(d.tiers || [])].sort((a, b) => a.qty - b.qty)) if (qty >= t.qty) off = Number(t.discount) || 0;
  return off;
}
function outletPrice(prices, id, qty) {
  const base = prices.map?.[id];
  if (base == null) return null;
  const d = prices.discounts?.[id];
  const off = discountOff(d, qty);
  const amount = d?.unit === 'percent' ? base * off / 100 : off;
  return { price: Math.round(Math.max(0, base - amount) * 10000) / 10000, base, amount, d };
}
// Re-price the lines filled from the price list (blank ones too), after any change.
export function reprice(ls, prices) {
  return ls.map(l => {
    if (!l.stock_item_id || !(l.auto || l.unit_price === '')) return l;
    const d = prices.discounts?.[l.stock_item_id];
    const qty = d?.basis === 'category'
      ? ls.filter(x => x.stock_item_id === l.stock_item_id).reduce((n, x) => n + (parseInt(x.quantity_units) || 0), 0)
      : (parseInt(l.quantity_units) || 0);
    const p = outletPrice(prices, l.stock_item_id, qty);
    if (p) return { ...l, unit_price: String(p.price), auto: true, base: p.base, off: p.amount };
    return l.auto ? { ...l, unit_price: '', auto: false, base: null, off: 0 } : l;
  });
}

// ── Order lines sub-form ──────────────────────────────────────────────────────
export function OrderLinesEditor({ lines, setLines, stockItems, prices = { map: {} } }) {
  function addLine() { setLines(ls => [...ls, { stock_item_id: '', quantity_units: '', unit_price: '' }]); }
  function removeLine(i) { setLines(ls => reprice(ls.filter((_, idx) => idx !== i), prices)); }
  function setLine(i, k, v) {
    // Picking a product or changing a quantity re-fills prices from the outlet's
    // price list and discounts; typing a price keeps it.
    setLines(ls => reprice(ls.map((l, idx) => (idx !== i ? l : { ...l, [k]: v, ...(k === 'unit_price' ? { auto: false, off: 0 } : {}) })), prices));
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Order lines *</label>
        <Btn size="sm" variant="ghost" type="button" onClick={addLine}>+ Add line</Btn>
      </div>
      {lines.length === 0 && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>No lines added yet.</p>}
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <Select value={l.stock_item_id} onChange={e => setLine(i, 'stock_item_id', e.target.value)} required>
            <option value="">Product...</option>
            {stockItems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <Input type="number" min="1" step="1" value={l.quantity_units} onChange={e => setLine(i, 'quantity_units', e.target.value)} placeholder="Qty (units)" required />
          <Input type="number" min="0" step="any" value={l.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} placeholder="Unit £" required />
          <button type="button" onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>&#215;</button>
          {l.auto && l.off ? (
            <div style={{ gridColumn: '1 / -1', marginTop: -4, fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>
              List £{Number(l.base).toFixed(2)} {l.off > 0 ? '−' : '+'} £{Math.abs(l.off).toFixed(2)} outlet discount
            </div>
          ) : null}
        </div>
      ))}
      {lines.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right', marginTop: 4 }}>
          Total: {fmtCurrency(lines.reduce((sum, l) => sum + (parseFloat(l.quantity_units) || 0) * (parseFloat(l.unit_price) || 0), 0))}
        </div>
      )}
    </div>
  );
}

// ── Create Sales Order modal ──────────────────────────────────────────────────
export function CreateOrderModal({ onClose, onSaved }) {
  const { data: stockData } = useApi('/stock');
  const stockItems = (stockData?.stock_items ?? stockData ?? []).filter(s => s.category === 'bottle_finished' && s.owner_type === 'PLD' && s.is_active !== false);
  const { data: outletData } = useApi('/own-brand/outlets?limit=all');
  const outlets = outletData?.outlets ?? outletData ?? [];

  const [form, setForm] = useState({
    source: 'IN_HOUSE', customer_type: 'outlet', outlet_id: '',
    delivery_method: 'DPD', notes: '', external_ref: '',
    delivery_address: { line1: '', line2: '', city: '', postcode: '', country: 'United Kingdom' },
    delivery_name: '', delivery_company: '', delivery_phone: '', delivery_email: '',
  });
  const [lines, setLines] = useState([{ stock_item_id: '', quantity_units: '', unit_price: '' }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // The outlet's price list: prices fill in as products are picked.
  const [prices, setPrices] = useState({ name: null, map: {}, discounts: {} });
  useEffect(() => {
    let live = true;
    if (form.customer_type !== 'outlet' || !form.outlet_id) {
      const none = { name: null, map: {}, discounts: {} };
      setPrices(none); setLines(ls => reprice(ls, none)); return undefined;
    }
    apiFetch(`/price-lists/for-outlet?outlet_id=${form.outlet_id}`).then(r => {
      if (!live) return;
      const p = { name: r?.name || null, map: r?.prices || {}, discounts: r?.discounts || {}, gross: !!r?.use_gross_price };
      setPrices(p);
      // Fill any line still blank (or filled from the last outlet's list).
      setLines(ls => reprice(ls, p));
    }).catch(() => {});
    // The outlet's delivery details: its address, contact and usual delivery method.
    const o = outlets.find(x => x.id === form.outlet_id);
    if (o) setForm(f => ({
      ...f,
      delivery_method: o.default_delivery_method || f.delivery_method,
      delivery_address: { line1: o.address_line1 || '', line2: o.address_line2 || '', city: o.town || '', postcode: o.postcode || '', country: o.country || 'United Kingdom' },
      delivery_name: o.primary_contact_name || '', delivery_company: o.name || '',
      delivery_phone: o.primary_contact_phone || '', delivery_email: o.primary_contact_email || '',
    }));
    return () => { live = false; };
  }, [form.outlet_id, form.customer_type]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setAddr = k => e => setForm(f => ({ ...f, delivery_address: { ...f.delivery_address, [k]: e.target.value } }));

  async function submit(e) {
    e.preventDefault();
    if (lines.length === 0 || !lines[0].stock_item_id) { setErr('Add at least one order line.'); return; }
    setSaving(true); setErr('');
    try {
      await apiFetch('/orders/sales', {
        method: 'POST',
        body: {
          ...form,
          outlet_id: form.outlet_id || undefined,
          lines: lines.map(({ auto, base, off, ...l }) => ({ ...l, quantity_units: parseInt(l.quantity_units), unit_price: parseFloat(l.unit_price) })),
        },
      });
      onSaved();
    } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="New Sales Order" onClose={onClose} width={680}>
      <form onSubmit={submit}>
        <ErrBanner msg={err} />
        <Grid2>
          <Field label="Source"><Select value={form.source} onChange={set('source')}>
            {['IN_HOUSE','EMAIL','SHOPIFY','COLLECTION'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
          </Select></Field>
          <Field label="Customer type"><Select value={form.customer_type} onChange={set('customer_type')}>
            <option value="outlet">Outlet (trade)</option>
            <option value="consumer">Consumer</option>
            <option value="client">Contract client</option>
          </Select></Field>
        </Grid2>
        {form.customer_type === 'outlet' && (
          <Field label="Outlet">
            <Select value={form.outlet_id} onChange={set('outlet_id')}>
              <option value="">Select outlet...</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Select>
            {form.outlet_id && <span style={{ fontSize: 12, color: prices.name ? 'var(--text-muted)' : '#b45309' }}>{prices.name ? `Prices from the ${prices.name} price list${prices.gross ? ' (include VAT)' : ''}` : 'No price list on this outlet — type prices in, or set one on the outlet'}</span>}
          </Field>
        )}
        <Field label="Customer's PO number"><Input value={form.external_ref} onChange={set('external_ref')} placeholder="Their purchase order number, if they gave one" maxLength={100} /></Field>
        <Field label="Delivery method">
          <Select value={form.delivery_method} onChange={set('delivery_method')}>
            {['DPD','PALLET_HAULAGE','CLIENT_COLLECT','IN_HOUSE'].map(m => <option key={m} value={m}>{m.replace(/_/g,' ')}</option>)}
          </Select>
        </Field>
        {form.delivery_method !== 'CLIENT_COLLECT' && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Delivery address</label>
            <Grid2>
              <Input placeholder="Line 1" value={form.delivery_address.line1} onChange={setAddr('line1')} />
              <Input placeholder="Line 2" value={form.delivery_address.line2} onChange={setAddr('line2')} />
            </Grid2>
            <div style={{ height: 8 }} />
            <Grid2>
              <Input placeholder="City" value={form.delivery_address.city} onChange={setAddr('city')} />
              <Input placeholder="Postcode" value={form.delivery_address.postcode} onChange={setAddr('postcode')} />
            </Grid2>
            <div style={{ height: 8 }} />
            <Grid2>
              <Input placeholder="Recipient name" value={form.delivery_name} onChange={set('delivery_name')} />
              <Input placeholder="Business name" value={form.delivery_company} onChange={set('delivery_company')} />
            </Grid2>
            <div style={{ height: 8 }} />
            <Grid2>
              <Input placeholder="Phone" value={form.delivery_phone} onChange={set('delivery_phone')} />
              <Input placeholder="Email" value={form.delivery_email} onChange={set('delivery_email')} />
            </Grid2>
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <OrderLinesEditor lines={lines} setLines={setLines} stockItems={stockItems} prices={prices} />
        </div>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set('notes')} /></Field>
        <BtnRow>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Draft Order'}</Btn>
        </BtnRow>
      </form>
    </Modal>
  );
}

// ── Order detail panel ────────────────────────────────────────────────────────
// Open a PDF from Blake in a new tab (packing slips, Joe 27 Oct 2026).
async function openPdf(path) {
  const tab = window.open('', '_blank');
  const res = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('pld_token') || ''}` } });
  if (!res.ok) { tab?.close(); let m = `HTTP ${res.status}`; try { m = (await res.json()).error || m; } catch { /* not json */ } throw new Error(m); }
  const url = URL.createObjectURL(await res.blob());
  if (tab) tab.location.href = url; else window.location.href = url;
}

export function OrderDetail({ orderId, onBack, onRefresh }) {
  const { data, loading, refetch } = useApi(`/orders/sales/${orderId}`, [orderId]);
  const order = data?.order_ref ? data : (data?.data ?? null);
  const [acting, setActing] = useState(false);
  const [err, setErr] = useState('');

  async function confirm() {
    if (!(await uiConfirm('Confirm this order? Stock will be reserved.'))) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/sales/${orderId}/confirm`, { method: 'POST', body: {} }); refetch(); onRefresh(); }
    catch(e) { setErr(e.message); } finally { setActing(false); }
  }

  async function despatch() {
    if (!(await uiConfirm('Mark this order as despatched? The stock comes off Blake now, and duty is recorded for anything leaving bond.'))) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/sales/${orderId}/despatch`, { method: 'POST', body: {} }); refetch(); onRefresh(); }
    catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  // A trade order that was really a client's own stock (e.g. THE PIG's) → fulfilment order.
  const [moving, setMoving] = useState(false);
  const [moveClient, setMoveClient] = useState('');
  const { data: clientsData } = useApi('/clients');
  const clientList = clientsData?.clients ?? clientsData?.data ?? (Array.isArray(clientsData) ? clientsData : []);
  function startMove() {
    const owner = order.lines?.find((l) => l.item_owner_type === 'CONTRACT' && l.item_owner_client_id)?.item_owner_client_id;
    setMoveClient(owner || ''); setMoving(true);
  }
  async function doMove() {
    const gone = ['DISPATCHED', 'DELIVERED'].includes(order.status);
    const name = clientList.find((c) => c.id === moveClient)?.name || 'the client';
    if (!(await uiConfirm(gone
      ? `Make ${order.order_ref} a fulfilment order for ${name}? It's already gone, so nothing is undone: the despatch and its duty move to ${name} (duty re-charged on their monthly bill) and this trade order is cancelled.`
      : `Make ${order.order_ref} a fulfilment order for ${name}? This trade order is cancelled (its stock let go) and a draft fulfilment order made with the same lines, to confirm.`, { okLabel: 'Move it' }))) return;
    setActing(true); setErr('');
    try {
      const r = await apiFetch(`/orders/sales/${orderId}/to-fulfilment`, { method: 'POST', body: { client_id: moveClient } });
      const d = r.data ?? r;
      await uiAlert(`Now fulfilment order ${d.order_ref}${d.duty_moved ? ` (£${Number(d.duty_moved).toFixed(2)} duty moved to ${name})` : ''}.${d.xero_note ? ` ${d.xero_note}` : ''}`);
      setMoving(false); onRefresh(); onBack();
    } catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  async function sendToXero() {
    setActing(true); setErr('');
    try { await apiFetch(`/xero/sales-orders/${orderId}`, { method: 'POST' }); refetch(); }
    catch (e) { setErr(e.message); refetch(); } finally { setActing(false); }
  }

  const { data: vansData } = useApi('/orders/vehicles');
  const vans = Array.isArray(vansData) ? vansData : (vansData?.data ?? []);
  async function setVehicle(reg) {
    setActing(true); setErr('');
    try { await apiFetch(`/orders/sales/${orderId}/vehicle`, { method: 'PATCH', body: { vehicle_reg: reg } }); refetch(); }
    catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  async function editRef() {
    const ref = await uiPrompt("Customer's purchase order number:", order.external_ref || '', { title: order.external_ref ? 'Change PO number' : 'Add PO number', okLabel: 'Save' });
    if (ref === null) return;
    setActing(true); setErr('');
    try {
      const r = await apiFetch(`/orders/sales/${orderId}/customer-ref`, { method: 'PATCH', body: { external_ref: ref } });
      refetch(); onRefresh?.();
      if ((r.data ?? r).in_xero) await uiAlert("Saved in Blake. This order's invoice is already in Xero, so change the reference there too.");
    } catch (e) { setErr(e.message); } finally { setActing(false); }
  }

  async function cancel() {
    const reason = (await uiPrompt('Reason for cancellation:'));
    if (reason === null) return;
    setActing(true); setErr('');
    try { await apiFetch(`/orders/sales/${orderId}/cancel`, { method: 'POST', body: { reason } }); refetch(); onRefresh(); }
    catch(e) { setErr(e.message); } finally { setActing(false); }
  }

  if (loading || !order) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading...</div>;

  const totalValue = order.lines?.reduce((sum, l) => sum + (parseFloat(l.quantity_units) || 0) * (parseFloat(l.unit_price) || 0), 0) ?? 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 4 }}>&#8592;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'monospace' }}>{order.order_ref}</h2>
            <StatusBadge status={order.status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {order.source} · {order.customer_type} · {order.delivery_method?.replace(/_/g,' ')} · Created {fmt(order.created_at)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {order.status !== 'CANCELLED' && <Btn size="sm" variant="ghost" onClick={() => openPdf(`/orders/sales/${orderId}/packing-slip.pdf`).catch((e) => setErr(e.message))}>📄 Packing slip</Btn>}
          {order.status !== 'CANCELLED' && <Btn size="sm" variant="ghost" title="Opens the despatch note: print it, or save it as a PDF" onClick={() => openPdf(`/orders/sales/${orderId}/despatch-note.pdf`).catch((e) => setErr(e.message))}>📄 Despatch note</Btn>}
          {order.status !== 'CANCELLED' && <PrintButton docType="despatch_note" path={`/orders/sales/${orderId}/despatch-note.pdf`} title={`Despatch note ${order.order_ref}`} label="Print despatch note" />}
          {order.status !== 'CANCELLED' && <Btn size="sm" variant="ghost" onClick={startMove} disabled={acting}>Make it a fulfilment order…</Btn>}
          {order.status === 'DRAFT' && <>
            <Btn size="sm" onClick={confirm} disabled={acting}>Confirm Order</Btn>
            <Btn size="sm" variant="danger" onClick={cancel} disabled={acting}>Cancel</Btn>
          </>}
          {order.status === 'CONFIRMED' && <><Btn size="sm" onClick={despatch} disabled={acting}>Mark despatched</Btn><Btn size="sm" variant="danger" onClick={cancel} disabled={acting}>Cancel</Btn></>}
        </div>
      </div>
      {err && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</p>}

      {/* Info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Outlet', value: order.outlet_name || order.outlet_id || '—' },
          { label: "Customer's PO number", value: <>{order.external_ref || '—'}{order.source !== 'SHOPIFY' && order.status !== 'CANCELLED' && <button type="button" onClick={editRef} disabled={acting} style={{ marginLeft: 8, background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 12, fontWeight: 500, textDecoration: 'underline' }}>{order.external_ref ? 'change' : 'add'}</button>}</> },
          { label: 'Delivery method', value: <>{order.delivery_method?.replace(/_/g,' ') || '—'}
            {order.delivery_method === 'IN_HOUSE' && vans.length > 1 && (
              <select value={order.vehicle_reg || ''} disabled={acting} onChange={e => setVehicle(e.target.value)} title="Which van — ticked on the despatch note"
                style={{ display: 'block', marginTop: 4, fontSize: 12, padding: '2px 6px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}>
                <option value="">Which van?</option>
                {vans.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            )}</> },
          { label: 'Delivery city', value: order.delivery_city || '—' },
          { label: 'Delivery postcode', value: order.delivery_postcode || '—' },
          { label: 'Total value', value: fmtCurrency(totalValue) },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
          </div>
        ))}
      </div>

      {order.notes && <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{order.notes}</div>}

      {moving && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 20, background: 'var(--surface-2)', fontSize: 13 }}>
          <strong>Make it a fulfilment order</strong> — for orders that were really a client's own stock.
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <span>Client:</span>
            <select value={moveClient} onChange={(e) => setMoveClient(e.target.value)} style={{ padding: '6px 10px', fontSize: 13, minWidth: 220 }}>
              <option value="">Choose…</option>
              {clientList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Btn size="sm" onClick={doMove} disabled={acting || !moveClient}>Move it</Btn>
            <Btn size="sm" variant="ghost" onClick={() => setMoving(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Xero: trade orders get a draft invoice when despatched (Joe, 27 Oct 2026). */}
      {order.customer_type === 'outlet' && ['DISPATCHED', 'DELIVERED'].includes(order.status) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 20, fontSize: 13, background: order.xero_invoice_id ? '#f0fdf4' : order.xero_error ? '#fef2f2' : 'var(--surface-2)' }}>
          <strong>Xero</strong>
          {order.xero_invoice_id ? <span style={{ color: '#166534' }}>✓ Draft invoice in Xero — approve it there</span>
            : order.xero_error ? <span style={{ color: '#991b1b' }}>Not sent: {order.xero_error}</span>
            : <span style={{ color: 'var(--text-muted)' }}>Not in Xero yet</span>}
          <span style={{ marginLeft: 'auto' }}>{order.xero_invoice_id
            ? <Btn size="sm" variant="ghost" onClick={sendToXero} disabled={acting} title="If the invoice was deleted or voided in Xero">{acting ? 'Sending…' : 'Send again'}</Btn>
            : <Btn size="sm" onClick={sendToXero} disabled={acting}>{acting ? 'Sending…' : 'Send to Xero'}</Btn>}</span>
        </div>
      )}

      {/* Lines table */}
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Order lines</h3>
      {!order.lines?.length ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No lines.</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Product', 'SKU', 'Qty (units)', 'Unit price', 'Line total'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{l.stock_item_name || l.stock_item_id}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{l.sku}</td>
                  <td style={{ padding: '10px 14px' }}>{l.quantity_units}</td>
                  <td style={{ padding: '10px 14px' }}>{fmtCurrency(l.unit_price)}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{fmtCurrency(l.quantity_units * l.unit_price)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-2)' }}>
                <td colSpan={4} style={{ padding: '10px 14px', fontWeight: 700, textAlign: 'right' }}>Total</td>
                <td style={{ padding: '10px 14px', fontWeight: 800 }}>{fmtCurrency(totalValue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <OrderConsignments kind="sales_order" orderId={orderId} />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const STATUSES = ['DRAFT', 'CONFIRMED', 'PICKING', 'DISPATCHED', 'DELIVERED', 'INVOICED', 'CANCELLED'];

export default function SalesOrders() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const qs = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
  const { data, loading, refetch } = useApi(`/orders/sales${qs}`, [statusFilter]);
  // useApi already hands back the response's `data`.
  const orders = Array.isArray(data) ? data : (data?.data ?? []);

  if (selectedId) return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 8px' }}>
      <OrderDetail orderId={selectedId} onBack={() => setSelectedId(null)} onRefresh={refetch} />
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      {showCreate && <CreateOrderModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Sales Orders</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Own-brand sales orders — create, confirm, and track despatch</p>
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ New Order</Btn>
      </div>

      {/* Status filter chips */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', ...STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)', color: statusFilter === s ? '#fff' : 'var(--text-muted)' }}>{s}</button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</p> : orders.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>No orders found</p>
          <p style={{ fontSize: 13, margin: 0 }}>Create your first sales order above.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Ref', 'Source', 'Customer', 'Outlet', 'Delivery', 'Lines', 'Total', 'Date', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} onClick={() => setSelectedId(o.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '11px 14px', fontWeight: 700, fontFamily: 'monospace' }}>{o.order_ref}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{o.source}</td>
                  <td style={{ padding: '11px 14px' }}>{o.customer_type}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{o.outlet_name || '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{o.delivery_method?.replace(/_/g,' ')}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{o.line_count ?? '—'}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{fmtCurrency(o.total_value)}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{fmt(o.created_at)}</td>
                  <td style={{ padding: '11px 14px' }}><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
